import { useQuery } from '@apollo/client';
import React, { useEffect, useId, useMemo, useState } from 'react';

import { Autocomplete, Checkbox, Text } from '@flixbus/honeycomb-react';

import { FETCH_DEADHEADS } from 'gql/deadheadSearch';
import { isoWindowAround, next48hIsoWindow } from 'utils/datetime/fnsTimeFormatter';
import { isDev, isStg } from 'utils/env';

import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';
import { STUB_DEADHEADS } from './stubDeadheads';

import styles from './DeadheadInputs.module.css';

const TEXT = CREATE_BREAKDOWN_FLOW_TEXT.deadhead;

const PAGE_SIZE = 50;

// Mirrors pages/RideListPage/components/DeadheadItem's getLocation:
// prefer "<code> (<name>)" when both are present; fall back to the address.
const formatLocation = (location) => {
	if (location?.stationDetails?.code && location?.stationDetails?.name) {
		return `${location.stationDetails.code} (${location.stationDetails.name})`;
	}
	return location?.address ?? location?.stationDetails?.code ?? '';
};

const buildOptionTitle = (dh) => {
	const route = [formatLocation(dh.departure), formatLocation(dh.arrival)]
		.filter(Boolean)
		.join(' → ');
	return `Schedule: ${dh.rideNumber} - ${route}`;
};

const buildOptionSubtitle = (dh) => (dh.type ? `Type: ${dh.type}` : '');

const deadheadToOption = (dh) => ({
	title: buildOptionTitle(dh),
	subtitle: buildOptionSubtitle(dh),
	value: dh.id,
	data: dh,
});

// Half-width of the search window around the incident time (hours).
const INCIDENT_WINDOW_HOURS = 12;

const deadheadDepartureWindow = (incidentTimestamp) => {
	const { from, to } =
		isoWindowAround(incidentTimestamp, INCIDENT_WINDOW_HOURS) ?? next48hIsoWindow();
	return { departureFromDateTime: from, departureToDateTime: to };
};

const isMockingEnv = () => isDev() || isStg();

/**
 * Deadhead picker (click-to-open dropdown; no typing search).
 *
 * `deadheadSearch` filter accepts only `fromCityIds`, `toCityIds`, and a
 * `departure*DateTime` range — there's no server-side text search — so this
 * is a plain dropdown, not an autocomplete. Loads deadheads departing in
 * the ±12h window around the incident time (falls back to now → now + 48h
 * if `incidentTimestamp` is missing).
 *
 * Each option renders in two lines:
 *   - `Schedule: <rideNumber> - <from> → <to>`
 *   - `Type: <type>` (backend-provided string, e.g. "9991 - Balance equipment")
 *
 * If the window returns nothing, renders the empty-state message and
 * emits nothing; the leaf validator keeps Next disabled. On dev/stg, a
 * "Use mocked results" checkbox sits under the empty-state message —
 * ticking it swaps in the local `STUB_DEADHEADS` list so the flow is
 * exercisable without a full dev-env data setup.
 *
 * `onChange` is called with `(deadheadId, deadhead)` so the parent leaf
 * carries the full deadhead object for the payload mapper.
 *
 * @param {{
 *   value: string|null,
 *   onChange: (deadheadId: string|null, deadhead: Object|null) => void,
 *   incidentTimestamp?: string|null,
 * }} props
 */
const DeadheadInputs = ({ value, onChange, incidentTimestamp = null }) => {
	const useMockCheckboxId = useId();
	const initialFilter = useMemo(
		() => deadheadDepartureWindow(incidentTimestamp),
		[incidentTimestamp]
	);

	const { data, loading, error } = useQuery(FETCH_DEADHEADS, {
		variables: {
			pagination: { page: 0, size: PAGE_SIZE },
			filter: initialFilter,
		},
		fetchPolicy: 'no-cache',
	});

	const [useMocked, setUseMocked] = useState(false);

	const liveRaw = data?.deadheadSearch?.results ?? [];
	const liveResults = useMemo(() => liveRaw, [data]); // eslint-disable-line react-hooks/exhaustive-deps

	const mockingAvailable = isMockingEnv() && liveResults.length === 0;
	const showingMocks = mockingAvailable && useMocked;

	const options = useMemo(() => {
		const source = showingMocks ? STUB_DEADHEADS : liveResults;
		return source.map(deadheadToOption);
	}, [showingMocks, liveResults]);

	const [inputValue, setInputValue] = useState('');

	const selectedValue = useMemo(() => {
		if (!value) return [];
		const match = options.find((opt) => opt.value === value);
		return match ? [{ title: match.title, value: match.value }] : [];
	}, [value, options]);

	useEffect(() => {
		if (selectedValue.length > 0 && !inputValue) {
			setInputValue(selectedValue[0].title);
		}
	}, [selectedValue, inputValue]);

	const handleChange = (selectedOptions) => {
		const selectedList = Array.isArray(selectedOptions) ? selectedOptions : [];
		if (selectedList.length === 0) {
			onChange(null, null);
			setInputValue('');
			return;
		}
		const [selected] = selectedList;
		const deadhead = options.find((opt) => opt.value === selected.value)?.data ?? null;
		onChange(selected.value, deadhead);
		setInputValue(selected.title);
	};

	const handleMockToggle = (event) => {
		const next = event?.target?.checked ?? false;
		setUseMocked(next);
		if (!next) {
			// Toggling stubs off while a stub-id is selected — clear the parent's
			// draft so the payload mapper never tries to submit a stub id.
			onChange(null, null);
			setInputValue('');
		}
	};

	// Empty state: no live results, and either mocking isn't available (prod)
	// or the user hasn't toggled the checkbox yet.
	if (!loading && !error && liveResults.length === 0 && !showingMocks) {
		return (
			<div className={styles.emptyState}>
				<Text data-testid="deadhead-empty" extraClasses={styles.empty}>
					{TEXT.empty}
				</Text>
				{mockingAvailable && (
					<Checkbox
						id={useMockCheckboxId}
						label={TEXT.useMockedResults}
						value="use-mocked"
						extraClasses={styles.mockToggle}
						checked={useMocked}
						onChange={handleMockToggle}
						data-testid="deadhead-use-mocked-toggle"
					/>
				)}
			</div>
		);
	}

	return (
		<Autocomplete
			{...(selectedValue.length ? { value: selectedValue } : {})}
			inputValue={inputValue}
			options={options}
			onChange={handleChange}
			comboboxProps={{
				loading,
				label: TEXT.label,
				placeholder: TEXT.placeholder,
				readOnly: true,
				valid: !error,
				infoError: error ? TEXT.error(error.message) : undefined,
			}}
			listBoxProps={{
				label: TEXT.label,
				optionsToDisplay: 5,
			}}
			data-testid="deadhead-autocomplete"
		/>
	);
};

export default DeadheadInputs;
